<script setup>
import { useConnectionStore } from '@/stores/ConnectionStore'
import { useRouter } from 'vue-router'
import { useGlobalStore } from '@/stores/GlobalStore'
import { useImportsStore } from '@/stores/ImportsStore'
import warningIcon from '@/assets/images/warning-small.svg'

const connectionStore = useConnectionStore()
const importsStore = useImportsStore()
const globalStore = useGlobalStore()
const router = useRouter()

const handleRedirectToExportHistory = (connection = 'ebay') => {
  router.replace(`/connections/${connection === 'ebay-direct' ? 'ebay' : connection}/history/export`)
  connectionStore.showExportToast = false
}

const handleTryAgain = () => {
  connectionStore.listedErrorModal = true
  connectionStore.showExportToast = false
}

const dismissToast = (idx) => {
  connectionStore.exportedChannelInfoList.splice(idx, 1)
  if (!connectionStore.exportedChannelInfoList.length) {
    connectionStore.showExportToast = false
  }
}
</script>

<template>
  <div class="fixed bottom-4 left-20 z-[1099] flex flex-col gap-3 items-start">
    <!-- Per-channel toasts (multi-channel listing) -->
    <TransitionGroup
      enter-active-class="transition duration-200 ease-out"
      enter-from-class="opacity-0 translate-y-2"
      enter-to-class="opacity-100 translate-y-0"
      leave-active-class="transition duration-150 ease-in"
      leave-from-class="opacity-100 translate-y-0"
      leave-to-class="opacity-0 translate-y-2"
    >
      <section
        v-for="(info, idx) in connectionStore.exportedChannelInfoList"
        :key="info.key ?? idx"
        class="shadow rounded-lg p-4 bg-white w-[372px] text-sm"
      >
        <div class="flex items-start gap-3">
          <img
            v-if="!globalStore.errors && info.miniLogo"
            :src="info.miniLogo"
            class="w-8 h-8 object-contain shrink-0"
          />
          <img v-if="globalStore.errors" :src="warningIcon" class="w-8 h-8 object-contain shrink-0" />

          <div class="flex-1 min-w-0">
            <header class="flex items-start justify-between mb-1">
              <h3 class="font-semibold text-sm leading-snug">
                {{
                  globalStore.errors
                    ? `Failed to list on ${info.name}`
                    : ['collx', 'ebay-direct', 'shopify', 'whatnot'].includes(info.key)
                      ? info.label || `Now Listing on ${info.name}`
                      : 'Listing In Progress'
                }}
              </h3>
              <button class="ml-2 shrink-0 text-gray-400 hover:text-gray-600" @click="dismissToast(idx)">
                <Icon icon="heroicons:x-mark" class="text-base" />
              </button>
            </header>

            <template v-if="!globalStore.errors">
              <p
                v-if="info.successful_cards || info.countOfCards"
                class="text-gray-600 text-xs mb-2"
              >
                {{
                  `${info.successful_cards || info.countOfCards} ${
                    ['collx', 'ebay', 'shopify', 'whatnot', 'ebay-direct'].includes(info.key) && !info.export
                      ? info.message || `items are being listed on ${info.name}. This may take a couple minutes.`
                      : `Listings exported to ${info.name}`
                  }`
                }}
              </p>
              <p v-if="info.failed_cards && !info.successful_cards" class="text-gray-600 text-xs mb-2">
                {{ info.message || `${info.failed_cards} cards could not be listed on ${info.name}.` }}
              </p>
            </template>
            <p v-else class="text-gray-600 text-xs mb-2">An unexpected error occurred</p>

            <button
              v-if="!globalStore.errors && !info.failed_cards"
              class="font-semibold text-tint-1 text-xs transition hover:text-tint-2"
              @click="handleRedirectToExportHistory(info.key)"
            >
              View in Export History
            </button>
            <button
              v-else-if="(globalStore.errors || info.failed_cards) && ['ebay', 'ebay-direct', 'whatnot'].includes(info.key)"
              class="font-semibold text-tint-1 text-xs transition hover:text-tint-2"
              @click="handleTryAgain"
            >
              View Details
            </button>
            <button v-else class="font-semibold text-tint-1 text-xs transition hover:text-tint-2">
              Try Again
            </button>
          </div>
        </div>
      </section>
    </TransitionGroup>

    <!-- Fallback: legacy single-toast (showExportToast set but list is empty — e.g. createExport flow) -->
    <Transition
      enter-active-class="transition duration-200 ease-out"
      enter-from-class="opacity-0 translate-y-2"
      enter-to-class="opacity-100 translate-y-0"
      leave-active-class="transition duration-150 ease-in"
      leave-from-class="opacity-100 translate-y-0"
      leave-to-class="opacity-0 translate-y-2"
    >
      <section
        v-if="connectionStore.showExportToast && !connectionStore.exportedChannelInfoList.length"
        class="shadow rounded-lg p-4 bg-white w-[372px] text-sm"
      >
        <div class="flex items-start gap-3">
          <img
            v-if="!globalStore.errors && connectionStore.exportedChannelInfo?.miniLogo"
            :src="connectionStore.exportedChannelInfo.miniLogo"
            class="w-8 h-8 object-contain shrink-0"
          />
          <img v-if="globalStore.errors" :src="warningIcon" class="w-8 h-8 object-contain shrink-0" />
          <div class="flex-1 min-w-0">
            <header class="flex items-start justify-between mb-1">
              <h3 class="font-semibold text-sm leading-snug">
                {{
                  globalStore.errors
                    ? `Failed to export to ${connectionStore.exportedChannelInfo?.name}`
                    : ['collx', 'ebay-direct', 'shopify', 'whatnot'].includes(connectionStore.exportedChannelInfo?.key)
                      ? connectionStore.exportedChannelInfo?.label || `Now Listing on ${connectionStore.exportedChannelInfo?.name}`
                      : 'Export In Progress'
                }}
              </h3>
              <button class="ml-2 shrink-0 text-gray-400 hover:text-gray-600" @click="connectionStore.showExportToast = false">
                <Icon icon="heroicons:x-mark" class="text-base" />
              </button>
            </header>
            <template v-if="!globalStore.errors">
              <p
                v-if="connectionStore.exportedChannelInfo?.successful_cards || connectionStore.exportedChannelInfo?.countOfCards"
                class="text-gray-600 text-xs mb-2"
              >
                {{
                  `${connectionStore.exportedChannelInfo.successful_cards || connectionStore.exportedChannelInfo.countOfCards} ${
                    ['collx', 'ebay', 'shopify', 'whatnot', 'ebay-direct'].includes(connectionStore.exportedChannelInfo?.key) && !connectionStore.exportedChannelInfo.export
                      ? connectionStore.exportedChannelInfo?.message || `items are being listed on ${connectionStore.exportedChannelInfo?.name}. This process may take a couple minutes.`
                      : `Listings has been exported to ${connectionStore.exportedChannelInfo?.name}`
                  }`
                }}
              </p>
              <p
                v-if="connectionStore.exportedChannelInfo?.failed_cards && !connectionStore.exportedChannelInfo?.successful_cards"
                class="text-gray-600 text-xs mb-2"
              >
                {{ connectionStore.exportedChannelInfo?.message || `${connectionStore.exportedChannelInfo?.failed_cards} cards could not be listed on ${connectionStore.exportedChannelInfo?.name}.` }}
              </p>
            </template>
            <p v-else class="text-gray-600 text-xs mb-2">An unexpected error occurred</p>
            <button
              v-if="!globalStore.errors && !connectionStore.exportedChannelInfo?.failed_cards"
              class="font-semibold text-tint-1 text-xs transition hover:text-tint-2"
              @click="handleRedirectToExportHistory(connectionStore.exportedChannelInfo?.key)"
            >
              View in Export History
            </button>
            <button
              v-else-if="(globalStore.errors || connectionStore.exportedChannelInfo?.failed_cards) && ['ebay', 'ebay-direct', 'whatnot'].includes(connectionStore.exportedChannelInfo?.key)"
              class="font-semibold text-tint-1 text-xs transition hover:text-tint-2"
              @click="handleTryAgain"
            >
              View Details
            </button>
            <button v-else class="font-semibold text-tint-1 text-xs transition hover:text-tint-2">Try Again</button>
          </div>
        </div>
      </section>
    </Transition>

    <!-- Import toast -->
    <Transition
      enter-active-class="transition duration-200 ease-out"
      enter-from-class="opacity-0 translate-y-2"
      enter-to-class="opacity-100 translate-y-0"
      leave-active-class="transition duration-150 ease-in"
      leave-from-class="opacity-100 translate-y-0"
      leave-to-class="opacity-0 translate-y-2"
    >
      <section
        v-if="importsStore.inventoryImportToast"
        class="shadow rounded-lg p-4 bg-white w-[372px] text-sm"
      >
        <div class="flex items-start gap-3">
          <img
            v-if="importsStore.importToChannel?.miniLogo"
            :src="importsStore.importToChannel.miniLogo"
            class="w-8 h-8 object-contain shrink-0"
          />
          <div class="flex-1 min-w-0">
            <header class="flex items-start justify-between mb-1">
              <h3 class="font-semibold text-sm">Importing Cards</h3>
              <button class="ml-2 shrink-0 text-gray-400 hover:text-gray-600" @click="importsStore.inventoryImportToast = false">
                <Icon icon="heroicons:x-mark" class="text-base" />
              </button>
            </header>
            <p class="text-gray-600 text-xs">
              Your import has been started, this can take a bit of time depending on how many items are being imported. You can see your upload progress above
            </p>
          </div>
        </div>
      </section>
    </Transition>
  </div>
</template>
